import React, { useState, useEffect } from "react";
import { motion, AnimatePresence, MotionConfig } from "motion/react";
import {
  Zap,
  ShieldCheck,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  Globe,
  MessageSquare,
  Repeat,
  Headphones,
  Camera,
  MessageCircle,
  Mail,
  Smartphone,
  CheckCircle2,
  Menu,
  X,
  Infinity,
  Cpu,
  BarChart3,
  Users,
  Layout,
  Activity,
  Shield,
  Search,
  Settings,
  Clock,
  ExternalLink,
  ChevronLeft,
  SearchIcon,
  Home,
  Tag,
  ShoppingBag,
  Package,
  PieChart,
  LifeBuoy,
  Plus,
  Bell,
  Play
} from "lucide-react";

const ZapLogo = () => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="logo-container"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="zap-logo">
        <div className="voltage-icon">
          <Zap size={28} fill="#2563eb" strokeWidth={3} />
        </div>

        <svg className="zap-arc" viewBox="0 0 100 100">
          <line
            className="voltage-arc"
            x1="10" y1="10" x2="90" y2="90"
            stroke="#2563eb"
            strokeLinecap="round"
          />
          <line
            className="voltage-arc"
            x1="90" y1="10" x2="10" y2="90"
            stroke="#2563eb"
            strokeLinecap="round"
            style={{ animationDelay: '0.1s' }}
          />
          <circle
            className="voltage-arc"
            cx="50"
            cy="50"
            r="20"
            fill="none"
            stroke="#2563eb"
            strokeWidth="1"
            style={{ animationDuration: '0.6s' }}
          />
        </svg>
      </div>
      <span className="logo-text">Volter</span>
    </div>
  );
};

const DropdownCard = ({ icon, title, desc, href }: { icon: any, title: string, desc: string, href: string }) => (
  <a href={href} className="dropdown-card">
    <div className="dropdown-icon">{icon}</div>
    <div className="dropdown-content">
      <div className="dropdown-title font-secondary">{title}</div>
      <div className="dropdown-desc">{desc}</div>
    </div>
  </a>
);

export default function App() {

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCommandTab, setActiveCommandTab] = useState("sentiment");
  const [showActionSuccess, setShowActionSuccess] = useState(false);

  const LOGIN_URL = window.location.hostname === "localhost" 
    ? "http://localhost:3000/login" 
    : "https://volter-by-beploo-services-nine.vercel.app/login";

  useEffect(() => {
    console.count("App Render");
  });


  const triggerAction = () => {
    setShowActionSuccess(true);
    setTimeout(() => setShowActionSuccess(false), 3000);
  };

  return (
    <div className="index">
      <header className="header">
        <div className="header-container">
          <ZapLogo />

          <nav className="desktopOnly nav">
            <div className="nav-item-wrapper">
              <a href="#features" className="navLink font-secondary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Features
                <ChevronDown size={14} strokeWidth={3} />
              </a>
              <div className="features-dropdown">
                <a href="#proxy" className="solutions-item">
                  <span className="solutions-title">Secure Admin Proxy</span>
                  <span className="solutions-desc">Zero-trust access for your Shopify backend.</span>
                </a>
                <a href="#pods" className="solutions-item">
                  <span className="solutions-title">Bilingual Pods</span>
                  <span className="solutions-desc">Native support in 12+ global languages.</span>
                </a>
                <a href="#limits" className="solutions-item">
                  <span className="solutions-title">Financial Limits</span>
                  <span className="solutions-desc">Hardcoded refund and credit safety nets.</span>
                </a>
                <a href="#unified" className="solutions-item">
                  <span className="solutions-title">Unified Inbox</span>
                  <span className="solutions-desc">One view for all social and email channels.</span>
                </a>
              </div>
            </div>
            <div className="nav-item-wrapper">
              <a href="#solutions" className="navLink font-secondary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Solutions
                <ChevronDown size={14} strokeWidth={3} />
              </a>
              <div className="solutions-dropdown">
                <a href="#expansion" className="solutions-item">
                  <span className="solutions-title">Market Expansion</span>
                  <span className="solutions-desc">Expand to US, UK, FR, CA, AUS, NZ, & EU.</span>
                </a>
                <a href="#risk" className="solutions-item">
                  <span className="solutions-title">Risk Management</span>
                  <span className="solutions-desc">Hardcoded Safety for your Shopify Admin.</span>
                </a>
                <a href="#scaling" className="solutions-item">
                  <span className="solutions-title">Rapid Scaling</span>
                  <span className="solutions-desc">Deploy Managed Pods in &lt; 24 Hours.</span>
                </a>
                <a href="#retention" className="solutions-item">
                  <span className="solutions-title">Retention & Sales</span>
                  <span className="solutions-desc">Convert Support Tickets into Repeat Buyers.</span>
                </a>
              </div>
            </div>
            <div className="nav-item-wrapper">
              <a href="#verticals" className="navLink font-secondary" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                Verticals
                <ChevronDown size={14} strokeWidth={3} />
              </a>
              <div className="verticals-dropdown">
                <div className="verticals-grid">
                  <a href="#fashion" className="solutions-item">
                    <span className="solutions-title">Fashion & Apparel</span>
                    <span className="solutions-desc">Returns, sizing, and styling support.</span>
                  </a>
                  <a href="#wellness" className="solutions-item">
                    <span className="solutions-title">Health & Wellness</span>
                    <span className="solutions-desc">Expert handling for sensitive queries.</span>
                  </a>
                  <a href="#lifestyle" className="solutions-item">
                    <span className="solutions-title">Home & Lifestyle</span>
                    <span className="solutions-desc">Support for decor and durable goods.</span>
                  </a>
                  <a href="#electronics" className="solutions-item">
                    <span className="solutions-title">Consumer Electronics</span>
                    <span className="solutions-desc">Technical troubleshooting and warranties.</span>
                  </a>
                  <a href="#beauty" className="solutions-item">
                    <span className="solutions-title">Luxury & Beauty</span>
                    <span className="solutions-desc">VIP-level concierge customer service.</span>
                  </a>
                </div>
              </div>
            </div>
            <div className="nav-item-wrapper">
              <a href="#pricing" className="navLink font-secondary">Pricing</a>
            </div>
            <div className="nav-item-wrapper">
              <a href="#network" className="navLink font-secondary">Network</a>
            </div>
          </nav>

          <div className="desktopOnly" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
            <a href={LOGIN_URL} className="navLink font-secondary" style={{ fontSize: '0.875rem' }}>Sign In</a>
            <button className="shimmerCTA font-secondary" style={{
              padding: '12px 28px',
              fontSize: '0.9375rem',
              borderRadius: '12px',
              background: 'var(--accent-primary)',
              boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)'
            }}>
              Deploy your fleet
            </button>
          </div>

          <button
            className="mobileMenuToggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        <div className={`mobileNav ${isMobileMenuOpen ? 'open' : ''}`}>
          <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="navLink font-secondary" style={{ fontSize: '1.5rem' }}>Features</a>
          <a href="#solutions" onClick={() => setIsMobileMenuOpen(false)} className="navLink font-secondary" style={{ fontSize: '1.5rem' }}>Solutions</a>
          <a href="#pricing" onClick={() => setIsMobileMenuOpen(false)} className="navLink font-secondary" style={{ fontSize: '1.5rem' }}>Pricing</a>
          <a href="#network" onClick={() => setIsMobileMenuOpen(false)} className="navLink font-secondary" style={{ fontSize: '1.5rem' }}>Network</a>
          <a href={LOGIN_URL} onClick={() => setIsMobileMenuOpen(false)} className="navLink font-secondary" style={{ fontSize: '1.5rem', color: 'var(--accent-primary)' }}>Sign In</a>
        </div>
      </header>

      <main>
        <section
          className="techHeroSection"
          style={{ minHeight: '85vh', position: 'relative', background: 'white' }}
          onMouseMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const x = ((e.clientX - rect.left) / rect.width) * 100;
            const y = ((e.clientY - rect.top) / rect.height) * 100;
            e.currentTarget.style.setProperty('--mouse-x', `${x}%`);
            e.currentTarget.style.setProperty('--mouse-y', `${y}%`);
          }}
        >
          {/* Background Layers */}
          <div className="noise-bg" style={{ zIndex: 1 }} />
          <div className="sharpy-bg" style={{ zIndex: 2 }} />
          <div className="techPulse" style={{ zIndex: 3 }} />

          <div className="alloy-wave" style={{ zIndex: 4 }}>
            <svg viewBox="0 0 1400 800" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
              {/* Ribbon 1: Glossy Blue */}
              <path
                d="M -300 500 Q 400 0 700 400 T 1700 500"
                className="alloy-ribbon animate-ribbon"
              />
              <path
                d="M -300 500 Q 400 0 700 400 T 1700 500"
                className="alloy-ribbon-gloss animate-ribbon"
                style={{ transform: 'translate3d(0, -15px, 0)' }}
              />
              <path
                d="M -300 500 Q 400 0 700 400 T 1700 500"
                className="alloy-ribbon-core animate-ribbon"
              />

              {/* Ribbon 2: Glossy Blue Secondary */}
              <path
                d="M -300 300 Q 500 900 1700 200"
                className="alloy-ribbon-vibrant animate-ribbon-alt"
              />
              <path
                d="M -300 300 Q 500 900 1700 200"
                className="alloy-ribbon-gloss animate-ribbon-alt"
                style={{ transform: 'translateY(-10px)' }}
              />
            </svg>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 10, width: '100%', maxWidth: '1200px', padding: '0 1rem' }}
          >
            <h1 className="heroTitleHighTech font-secondary" style={{ fontSize: 'clamp(2.5rem, 9vw, 6.5rem)', fontWeight: 800, letterSpacing: '-0.05em', lineHeight: 0.95, color: '#0f172a', marginBottom: '2rem' }}>
              Stop Managing Your <br />
              <span style={{ color: 'var(--accent-primary)' }}>Support Inbox.</span>
            </h1>

            <p className="techSubheading" style={{ color: '#475569', fontSize: 'clamp(1rem, 4vw, 1.4rem)', maxWidth: '850px', marginBottom: '3rem', lineHeight: 1.4, fontWeight: 500 }}>
              Real Agents. Your Brand Voice. Fixed Flat Price. While you and your competitors are paying $4000+/month for single in-house CS Agent to manage it -- Volter is everything from $599/month incl per agent.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem', alignItems: 'center', width: '100%' }}>
              <button className="shimmerCTA font-secondary" style={{
                background: 'var(--accent-primary)',
                borderRadius: '12px',
                padding: '1.25rem 3.5rem',
                fontSize: 'clamp(1rem, 3vw, 1.25rem)',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                boxShadow: '0 20px 40px rgba(37, 99, 235, 0.3)',
                width: 'fit-content'
              }}>
                Deploy your fleet
                <ArrowRight size={24} />
              </button>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="desktopOnly"
                style={{ display: 'flex', gap: '3.5rem', alignItems: 'center', opacity: 0.9 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <img src="/shopify/logo-color-white-bg.svg" alt="Shopify Partner" style={{ height: '22px' }} />
                  <span style={{ fontFamily: '"Shopify Sans", -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#1a1c1d' }}>Partner</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <img src="/shopify/glyph-color.svg" alt="Shopify Academy" style={{ height: '24px' }} />
                  <span style={{ fontFamily: '"Shopify Sans", -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#1a1c1d' }}>Academy Certified</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <img src="/shopify/glyph-black.svg" alt="Only Human First CX on Shopify." style={{ height: '24px', opacity: 0.8 }} />
                  <span style={{ fontFamily: '"Shopify Sans", -apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#1a1c1d' }}>Only Human First CX on Shopify.</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </section>

        <section style={{ 
          padding: '0.75rem 0', 
          background: 'white', 
          borderTop: '1px solid rgba(0,0,0,0.06)',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          position: 'relative',
          zIndex: 10
        }}>
          <div className="container" style={{ marginBottom: '0.75rem', textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '0.65rem', 
              fontWeight: 800, 
              textTransform: 'uppercase', 
              letterSpacing: '0.3em', 
              color: '#94a3b8',
              marginBottom: '0.15rem'
            }}>
              Our Channels
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0', background: '#f8fafc', overflow: 'hidden' }}>
            <div style={{ height: '1px', background: 'rgba(0,0,0,0.03)', width: '100%' }} />
            <Marquee items={CHANNELS} speed={90} />
            <div style={{ height: '1px', background: 'rgba(0,0,0,0.03)', width: '100%' }} />
            <Marquee items={CHANNELS_ALT} speed={100} reverse={true} />
            <div style={{ height: '1px', background: 'rgba(0,0,0,0.03)', width: '100%' }} />
          </div>
        </section>






        {/* Shopify Admin Showcase - Loom Video */}
        <section className="container" style={{ padding: '6rem 2rem 8rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{ width: '32px', height: '1px', background: 'var(--accent-primary)' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'var(--accent-primary)' }}>
                Direct Integration
              </span>
              <div style={{ width: '32px', height: '1px', background: 'var(--accent-primary)' }} />
            </div>
            <h2 className="font-secondary" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '1.5rem', lineHeight: 1.1 }}>
              Operations floor inside <br />
              your shopify admin
            </h2>
            <p style={{ color: '#64748b', fontSize: '1.25rem', maxWidth: '700px', margin: '0 auto', lineHeight: 1.5 }}>
              admin.shopify.com/volter.sbs/merchant[] — Watch how we manage your entire support fleet directly from where you run your business.
            </p>
          </div>

          <div style={{ 
            maxWidth: '1200px', 
            margin: '0 auto', 
            position: 'relative',
            borderRadius: '24px',
            overflow: 'hidden',
            boxShadow: '0 40px 100px rgba(15, 23, 42, 0.15)',
            background: '#f8fafc',
            border: '1px solid rgba(15, 23, 42, 0.05)'
          }}>
            <div style={{ 
              position: 'relative', 
              paddingBottom: '56.25%', /* 16:9 Aspect Ratio */
              height: 0,
              overflow: 'hidden'
            }}>
              <iframe 
                src="https://www.loom.com/embed/f55e4fa91d284537b9b960803124c563" 
                frameBorder="0" 
                webkitallowfullscreen="true" 
                mozallowfullscreen="true" 
                allowFullScreen 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: '12px'
                }}
              />
            </div>
          </div>
        </section>

        <section className="container">
          <div className="metricsGrid">
            <MetricCard value="< 3 min" label="FRT SLA" />
            <MetricCard value="90%" label="CSAT" />
            <MetricCard value="100%" label="human CS Agents" />
            <MetricCard value="24/7" label="SHIFT COVERAGE" />
          </div>
        </section>

        <section id="features" className="container" style={{ padding: '8rem 2rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{ width: '32px', height: '1px', background: 'var(--accent-primary)' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'var(--accent-primary)' }}>
                System Features
              </span>
              <div style={{ width: '32px', height: '1px', background: 'var(--accent-primary)' }} />
            </div>
            <h2 className="font-secondary" style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '1.5rem', lineHeight: 1.1 }}>
              Engineered for <br />
              Enterprise Security
            </h2>
            <p style={{ color: '#64748b', fontSize: '1.25rem', maxWidth: '700px', margin: '0 auto', lineHeight: 1.5 }}>
              Professional offshore support that bridges UK standards with global scalability, hardcoded directly into your Shopify admin.
            </p>
          </div>

          <div className="featureGrid">
            <FeatureCard
              title="Secure Admin Proxy"
              desc="Zero-trust access logic that protects your customer data and Shopify backend from unauthorized access."
            />
            <FeatureCard
              title="Bilingual Support Pods"
              desc="Professional human agents providing 24/7 coverage in 12+ global languages, tailored to your brand voice."
            />
            <FeatureCard
              title="Hardcoded Financial Limits"
              desc="Enforced safety nets for refunds, credits, and discounts, ensuring strict financial control across your fleet."
            />
            <FeatureCard
              title="Omnichannel Unified Inbox"
              desc="A single, lightning-fast view for all your Instagram, WhatsApp, Email, and SMS customer conversations."
            />
            <FeatureCard
              title="Slack-Integrated Escalations"
              desc="Real-time transparency with automated escalations directly into your team's Slack or Discord channels."
            />
            <FeatureCard
              title="Managed Training & Onboarding"
              desc="Total hands-off experience. We handle the hiring, training, and bilingual SOP development for your store."
            />
          </div>
        </section>
      </main>

      <footer className="colossal-footer">
        <div className="footer-grid">
          {/* Column 1: Brand */}
          <div className="footer-section">
            <div className="footer-logo-container">
              <div style={{ color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Infinity size={32} strokeWidth={2.5} />
              </div>
              <span className="footer-logo-text">Beploo LTD.</span>
            </div>
            <p style={{ color: '#64748b', fontSize: '0.9375rem', lineHeight: 1.6, maxWidth: '280px' }}>
              Secure Shopify Admin Proxy & Managed CX Pods.<br />
              Built for Giants. Guarded by Code.
            </p>
            <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <p style={{ fontSize: '0.8125rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Direct Line</p>
                <a href="tel:+447478036438" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--text-primary)', textDecoration: 'none' }}>+44 7478036438</a>
              </div>
              <div>
                <p style={{ fontSize: '0.8125rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', marginBottom: '0.4rem' }}>Email Handle</p>
                <a href="mailto:partners@volter.sbs" style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--accent-primary)', textDecoration: 'none' }}>partners@volter.sbs</a>
              </div>
            </div>
          </div>

          {/* Column 2: UK Headquarters */}
          <div className="footer-section">
            <h4 className="footer-address-title">UK Headquarters</h4>
            <address className="footer-address">
              Beploo Ltd.<br />
              60 Tottenham Court Road<br />
              Office 854<br />
              Fitzrovia, London W1T 2EW<br />
              United Kingdom
            </address>
          </div>

          {/* Column 3: Global Operations */}
          <div className="footer-section">
            <h4 className="footer-address-title">Operations Floor</h4>
            <address className="footer-address">
              Wixorca Exim LLP<br />
              White Town<br />
              Pondicherry 605001<br />
              India
            </address>
          </div>

          {/* Column 4: Quick Links */}
          <div className="footer-section">
            <h4 className="footer-address-title">Ecosystem</h4>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <a href="#features" className="footer-link">System Features</a>
              <a href="#solutions" className="footer-link">Market Solutions</a>
              <a href="#network" className="footer-link">Global Network</a>
              <a href={LOGIN_URL} className="footer-link">Agent Login</a>
            </nav>
          </div>
        </div>

        <div style={{ maxWidth: '1300px', margin: '4rem auto 0', paddingTop: '2rem', borderTop: '1px solid rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <p style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>
            © {new Date().getFullYear()} Beploo LTD. All Rights Reserved. Registered in England & Wales.
          </p>
          <div style={{ display: 'flex', gap: '2rem' }}>
            <a href="#" className="footer-link" style={{ fontSize: '0.8125rem' }}>Privacy Policy</a>
            <a href="#" className="footer-link" style={{ fontSize: '0.8125rem' }}>Terms of Service</a>
          </div>
        </div>
      </footer>
      </div>
    );
}

function EcosystemItem({ logo, text }: { logo: React.ReactNode, text: string }) {
  return (
    <div className="ecosystem-item">
      <div className="ecosystem-logo">{logo}</div>
      <span className="ecosystem-text">{text}</span>
    </div>
  );
}

function SidebarItem({ icon, text, active = false }: { icon: React.ReactNode, text: string, active?: boolean }) {
  return (
    <div className={`sidebarItem ${active ? 'active' : ''}`}>
      <div style={{ color: active ? 'white' : '#c1c1c1' }}>{icon}</div>
      <span className="sidebarText">{text}</span>
      {active && <div style={{ marginLeft: 'auto', width: '6px', height: '6px', background: '#10b981', borderRadius: '50%' }} />}
    </div>
  );
}


function MetricCard({ value, label }: { value: string, label: string }) {
  return (
    <div className="metricCard">
      <span className="metricValue">{value}</span>
      <span className="metricLabel">{label}</span>
    </div>
  );
}

function FeatureCard({ title, desc }: { title: string, desc: string }) {
  return (
    <motion.div
      whileHover={{ 
        y: -8, 
        boxShadow: '0 30px 60px rgba(0,0,0,0.08)',
        borderColor: 'rgba(37, 99, 235, 0.2)' 
      }}
      style={{
        padding: '3rem',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: '1px solid #e2e8f0',
        borderRadius: '24px',
        transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
        position: 'relative'
      }}
    >
      <h3 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1.25rem', color: '#020617', letterSpacing: '-0.02em' }}>{title}</h3>
      <p style={{ color: '#475569', lineHeight: 1.7, fontSize: '1rem' }}>{desc}</p>
    </motion.div>
  );
}

const CHANNELS = [
  { name: "Slack", logo: "/slack.png", subtext: "connect", type: 'stack' },
  { name: "WhatsApp Business", logo: "whatsapp", type: 'side' },
  { name: "Discord", logo: "/discord.png", type: 'logo' },
  { name: "Telegram", logo: "telegram", type: 'side' },
  { name: "Line", logo: "/line.png", type: 'logo' },
];

const CHANNELS_ALT = [
  { name: "Telegram", logo: "telegram", type: 'side' },
  { name: "Discord", logo: "/discord.png", type: 'logo' },
  { name: "WhatsApp Business", logo: "whatsapp", type: 'side' },
  { name: "Slack", logo: "/slack.png", subtext: "connect", type: 'stack' },
  { name: "Line", logo: "/line.png", type: 'logo' },
];


function Marquee({ items, reverse = false, speed = 50 }: { items: any[], reverse?: boolean, speed?: number }) {
  return (
    <div className="marquee-container">
      <div 
        className={`marquee-track ${reverse ? 'reverse' : ''}`}
        style={{ animationDuration: `${speed}s` }}
      >
        {[...items, ...items, ...items, ...items, ...items, ...items].map((item, i) => (
          <div key={i} style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '75px',
            padding: '0 3.5rem',
          }}>
            {item.type === 'stack' ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
                <img src={item.logo} alt={item.name} style={{ height: '32px', width: 'auto' }} />
                <span style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: 600, 
                  color: '#4a154b', 
                  fontFamily: '"Slack-Larsseit", "Larsseit", sans-serif' 
                }}>
                  {item.subtext}
                </span>
              </div>
            ) : item.type === 'side' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {item.logo === 'whatsapp' ? (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="#25D366">
                    <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.438 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
                  </svg>
                ) : (
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="#0088cc">
                    <path d="M11.944 0C5.344 0 0 5.344 0 11.944c0 6.6 5.344 11.944 11.944 11.944 6.6 0 11.944-5.344 11.944-11.944C23.888 5.344 18.544 0 11.944 0zm5.834 8.232l-1.956 9.21c-.147.66-.543.822-1.096.512l-2.98-2.195-1.438 1.384c-.16.16-.293.293-.6.293l.214-3.037 5.526-4.992c.24-.213-.053-.332-.373-.12L8.243 13.56l-2.946-.92c-.64-.2-.653-.64.133-.946l11.517-4.437c.533-.193 1 .127.83 1.075z"/>
                  </svg>
                )}
                <span style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: 700, 
                  color: item.name === 'WhatsApp Business' ? '#075E54' : '#0088cc',
                  fontFamily: item.name === 'WhatsApp Business' ? '"Helvetica Neue", Helvetica, Arial, sans-serif' : 'system-ui, -apple-system, sans-serif'
                }}>
                  {item.name}
                </span>
              </div>
            ) : (
              <img src={item.logo} alt={item.name} style={{ height: '36px', width: 'auto' }} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
